import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import useStyles from './member-matches.styles';

// Whether the applicant matches an existing member: none, already linked to one, or possible
// matches to review. "See all matches" opens the member search on Modify Quote; from Checkout it
// goes back there first, where that search lives.
const MemberMatches = ({ fromCheckout, offerId, history }) => {
  const classes = useStyles();
  const {
    offer: { loading, offer },
    matchSearch
  } = useStore();

  const seeAllMatches = () => {
    matchSearch.setShowCustomerMatches(true);
    if (fromCheckout) {
      history.push(`/offer/${offerId}`);
    }
  };

  if (matchSearch.loading || (loading && !offer) || !matchSearch.searchString) {
    return (
      <div className={classes.card}>
        <Loading noBackground />
      </div>
    );
  }

  const linkedCustomerId = offer?.quote?.existingCustomer?.id;
  if (linkedCustomerId) {
    const linkedCustomer = matchSearch.customersResults?.hits?.[0]?.attributes;
    if (!linkedCustomer) {
      return null;
    }
    return (
      <div className={classNames(classes.card, classes.linked)}>
        <Link
          to={`/customer/${linkedCustomerId}`}
          target="_blank"
          rel="noreferrer noopener"
          className={classes.primaryText}
        >
          Linked to member {linkedCustomer.name} {linkedCustomer.family_name} - ID {linkedCustomerId}
        </Link>
        <button type="button" className={classes.seeAllMatches} onClick={seeAllMatches}>
          See all matches
        </button>
      </div>
    );
  }

  const matchCount = matchSearch.customersResults?.nbHits || 0;
  // The automatic search uses the applicant's name plus the offer's state, so a member whose account
  // is in another state comes back as 0 hits. Staff still need the search to find and link them.
  if (!matchCount) {
    return (
      <div className={classNames(classes.card, classes.empty)}>
        <span className={classes.primaryText}>No matches to current members</span>
        <button type="button" className={classes.seeAllMatches} onClick={seeAllMatches}>
          Search members
        </button>
      </div>
    );
  }

  return (
    <button type="button" className={classNames(classes.card, classes.found)} onClick={seeAllMatches}>
      <span className={classes.primaryText}>
        We&rsquo;ve found {matchCount} possible {matchCount === 1 ? 'match' : 'matches'}
      </span>
      <span className={classes.seeAllMatches}>See all matches</span>
    </button>
  );
};

MemberMatches.propTypes = {
  fromCheckout: PropTypes.bool.isRequired,
  offerId: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired
};

export default observer(MemberMatches);
